import moment from "moment";

export interface AiQuota {
  aiQuotaType?: number;
  remainingMinutes?: number;
  allocatedMinutes?: number;
  usedMinutes?: number;
  cycleEnd?: any;
}

/**
 * Lấy ngày reset gói cước (format DD/MM/YYYY) từ cycleEnd
 * @param cycleEnd Trực tiếp từ customer.aiQuota.cycleEnd hoặc tương đương
 * @returns chuỗi ngày đã format, hoặc rỗng nếu không có cycleEnd
 */
export function formatAiQuotaCycleStart(cycleEnd: any): string {
  if (!cycleEnd) return "";
  return moment(cycleEnd).format("DD/MM/YYYY");
}

export function isAiUnlimited(aiQuota?: AiQuota | null): boolean {
  return aiQuota?.aiQuotaType === 3;
}

export function getAiQuotaRemainingMinutes(aiQuota?: AiQuota | null): number {
  const minutes = aiQuota?.remainingMinutes || 0;
  return Math.round(minutes * 100) / 100;
}

export function getAiQuotaTotalMinutes(aiQuota?: AiQuota | null): number {
  if (!aiQuota) return 0;
  const total = aiQuota.allocatedMinutes ?? 0;
  return Math.round(total * 100) / 100;
}

export function getAiQuotaUsedMinutes(aiQuota?: AiQuota | null): number {
  if (!aiQuota) return 0;
  const used = aiQuota.usedMinutes ?? 0;
  return Math.round(used * 100) / 100;
}

export function getAiQuotaProgressMax(aiQuota?: AiQuota | null): number {
  if (!aiQuota) return 1;
  if (isAiUnlimited(aiQuota)) return 100;
  return getAiQuotaTotalMinutes(aiQuota) || 1;
}

export function getAiQuotaProgressValue(aiQuota?: AiQuota | null): number {
  if (!aiQuota) return 0;
  if (isAiUnlimited(aiQuota)) return 100;
  return getAiQuotaUsedMinutes(aiQuota);
}

export function getAiQuotaProgressLabel(aiQuota?: AiQuota | null): string {
  if (!aiQuota) return '0%';
  if (isAiUnlimited(aiQuota)) return '';
  const total = getAiQuotaTotalMinutes(aiQuota) || 1;
  return `${Math.round((getAiQuotaUsedMinutes(aiQuota) / total) * 100)}%`;
}

export function getAiQuotaStatusColor(aiQuota?: AiQuota | null): string {
  if (!aiQuota) return '#dc3545';
  if (isAiUnlimited(aiQuota)) return '#28a745';
  const total = getAiQuotaTotalMinutes(aiQuota);
  if (!total) return '#dc3545';
  const pct = (getAiQuotaUsedMinutes(aiQuota) / total) * 100;
  if (pct >= 90) return '#dc3545';
  if (pct >= 70) return '#ffc107';
  return '#28a745';
}

/**
 * Kiểm tra xem khách hàng có tính năng AI hay không (bằng cách kiểm tra trong customerFeatures)
 * @param customerFeatures Danh sách tính năng của khách hàng
 * @returns true nếu có ít nhất 1 tính năng ALLOW_SUMMARY hoặc ALLOW_LIVECAPTION đang active
 */
export function hasAIFeature(customerFeatures?: any[] | null): boolean {
  return customerFeatures?.some(
    f => (f.featureCode === 'ALLOW_SUMMARY' || f.featureCode === 'ALLOW_LIVECAPTION') && (f.isActive === 1 || f.isActive === '1' || f.isActive === true)
  ) ?? false;
}

/**
 * Kiểm tra xem có hiển thị thông tin AI Quota hay không
 * @param hasAiFeature Kết quả từ hasAIFeature
 * @param aiQuota Object aiQuota của khách hàng
 * @returns true nếu có tính năng AI và aiQuota hợp lệ (khác 0 và không null/undefined)
 */
export function showAiQuota(hasAiFeature: boolean, aiQuota?: AiQuota | null): boolean {
  return hasAiFeature && !!(aiQuota && aiQuota.aiQuotaType !== 0 && aiQuota.aiQuotaType != null);
}
